import { Button } from '@app/app/_components/Button/Button';
import { Card } from '@app/app/_components/Card/Card';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Bank } from '@app/domain/model/Bank';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { initializeSebAuth } from '@app/service/auth/actions/initializeSebAuth';
import { initializeSwedbankAuth } from '@app/service/auth/actions/initializeSwedbankAuth';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { submitSebAuthForm } from '@app/app/_widgets/Login/submitSebAuthForm';

interface Props {
    swedbankLogoSrc?: string;
    swedbankAuth?: boolean;
    sebLogoSrc?: string;
    sebAuth?: boolean;
}

export function EBanksLoginSection({ swedbankLogoSrc, swedbankAuth, sebLogoSrc, sebAuth }: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const [selectedAuthMethod, setSelectedAuthMethod] = useState<Bank | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const initializeAuth = useCallback(async () => {
        if (selectedAuthMethod == null) {
            return;
        }

        if (selectedAuthMethod === Bank.Swedbank) {
            setIsLoading(true);
            const response = await initializeSwedbankAuth();

            if (response.status === FormStatus.Success && response.data) {
                window.location.href = response.data;
                setIsLoading(false);
            }
        }

        if (selectedAuthMethod === Bank.Seb) {
            setIsLoading(true);
            const response = await initializeSebAuth();

            if (response.status === FormStatus.Success && response.data) {
                await submitSebAuthForm(response.data, parameters.auth.sebAuthUrl);
                setIsLoading(false);
            }
        }

        setIsLoading(false);
    }, [parameters.auth.sebAuthUrl, selectedAuthMethod]);

    const selectSwedbank = useCallback(() => {
        setSelectedAuthMethod(Bank.Swedbank);
    }, []);

    const selectSeb = useCallback(() => {
        setSelectedAuthMethod(Bank.Seb);
    }, []);

    return (
        <div className="flex flex-col gap-9">
            <div className="flex flex-col gap-1">
                <Paragraph isGray>{t('auth.chooseBank')}</Paragraph>
                <div className="flex w-full flex-row gap-4">
                    {swedbankAuth && (
                        <GhostButton onClick={selectSwedbank}>
                            <Card
                                className={twMerge(
                                    'box-border flex h-14 w-32 items-center justify-center border border-transparent',
                                    selectedAuthMethod === Bank.Swedbank && 'border-primary-500',
                                )}>
                                <Image
                                    src={swedbankLogoSrc || ''}
                                    alt="Swedbank logo"
                                    className="object-contain"
                                    width={100}
                                    height={22}
                                />
                            </Card>
                        </GhostButton>
                    )}
                    {sebAuth && (
                        <GhostButton onClick={selectSeb}>
                            <Card
                                className={twMerge(
                                    'box-border flex h-14 w-32 items-center justify-center border border-transparent',
                                    selectedAuthMethod === Bank.Seb && 'border-primary-500',
                                )}>
                                <Image
                                    src={sebLogoSrc || ''}
                                    alt="Seb logo"
                                    className="object-contain"
                                    width={50}
                                    height={22}
                                />
                            </Card>
                        </GhostButton>
                    )}
                </div>
            </div>
            <Button className="w-full" onClick={initializeAuth} isLoading={isLoading}>
                {t('auth.login')}
            </Button>
        </div>
    );
}
